import {
  type FunctionComponent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Dropdown, Input, Link, newAsyncDataEnvelope,
} from '@dr.pogodin/react-utils';

import PageWithVideoBackground from 'components/PageWithVideoBackground';
import Throbber from 'components/Throbber';
import { useExperiences } from 'hooks/useExperiences';

import { usePastSessions } from 'hooks/usePastSessions';
import {
  E_EXPERIENCE,
  type ExperienceT,
  MODALITIES,
  type ModalityT,
  type SessionT,
  getId,
  initExperienceData,
  useGlobalStateObject,
} from 'utils';

import IconPlus from 'assets/images/icon-plus-24px.svg';
import IconSearch from 'assets/images/icon-search-24px.svg';

import Item from './Item';

import searchInputTheme from './search-input.scss';
import sortDropdownTheme from './sort-dropdown.scss';

import './style.scss';

const SORT_OPTIONS = [
  { name: 'Newest', value: 'newest' },
  { name: 'Oldest', value: 'oldest' },
] as const;

type SortOrderT = typeof SORT_OPTIONS[number]['value'];

type SessionExperienceT = {
  experience: ExperienceT;
  session: SessionT;
};

const PastSessions: FunctionComponent = () => {
  const gs = useGlobalStateObject();

  const containerRef = useRef<HTMLDivElement>(null);
  const loaderRef = useRef<HTMLDivElement>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState<SortOrderT>('newest');
  const [selectedModality, setSelectedModality] = useState<'All' | ModalityT>('All');

  const sessions = usePastSessions();

  const experienceIds: string[] = [];
  for (const session of sessions.sessions) {
    experienceIds.push(session.experienceId);
  }

  const experiences = useExperiences(experienceIds);

  const pairs: SessionExperienceT[] = [];
  for (const session of sessions.sessions) {
    const experience = experiences.items[session.experienceId]?.data;
    if (experience) pairs.push({ experience, session });
  }

  const query = searchQuery.trim().toLowerCase();
  const filteredPairs = pairs.filter(({ experience }) => {
    if (query && !experience.title?.toLowerCase().includes(query)) return false;
    if (
      selectedModality !== 'All'
      && !experience.videos.some(
        (video) => video.modalities.includes(selectedModality),
      )
    ) return false;
    return true;
  });

  filteredPairs.sort((a, b) => {
    const at = a.session.startedAt ?? 0;
    const bt = b.session.startedAt ?? 0;
    return sortOrder === 'newest' ? bt - at : at - bt;
  });

  const sessionNodes: ReactNode[] = [];
  for (const { experience, session } of filteredPairs) {
    sessionNodes.push(
      <Item
        experience={experience}
        key={getId(session)}
        session={session}
      />,
    );
  }

  let loadMore: (() => void) | undefined;

  const loading = experiences.loading || sessions.loading;

  if (!loading && sessions.loadNextPage) {
    loadMore = sessions.loadNextPage;
    sessionNodes.push(
      <div
        key="load-more"
        ref={loaderRef}
      />,
    );
  }

  // NOTE: It is slightly beneficial to render this throbber even prior
  // to the initiation of the loading - otherwise loading related operations
  // might visibly delay the appearance of the throbber (at least in dev mode).
  if (loading || loadMore) {
    sessionNodes.push(
      <div key="throbber" styleName="throbberContainer"><Throbber /></div>,
    );
  } else if (!filteredPairs.length) {
    sessionNodes.push(
      <div key="empty" styleName="emptyState">
        {pairs.length
          ? 'No experiences match your search or filter.'
          : 'You have no past experiences yet.'}
      </div>,
    );
  }

  useEffect(() => {
    let observer: IntersectionObserver | undefined;
    if (loadMore) {
      if (!containerRef.current || !loaderRef.current) {
        throw Error('Internal error');
      }

      observer = new IntersectionObserver(([e]) => {
        if (e?.isIntersecting) loadMore();
      }, { root: containerRef.current });
      observer.observe(loaderRef.current);
    }
    return () => {
      observer?.disconnect();
    };
  }, [loadMore]);

  return (
    <PageWithVideoBackground>
      <div styleName="pageContainer">
        <div ref={containerRef} styleName="contentWrapper">
          <div styleName="mainCol">
            <div styleName="header">
              <div styleName="headerText">
                <h1>My Experiences</h1>
                <h2>Your Previous Wellness Experiences</h2>
              </div>
              <Link
                aria-label="Create New Experience"
                onClick={() => {
                  gs.set('experience', newAsyncDataEnvelope({
                    checkInId: null,
                    experience: initExperienceData(),
                    sessionKind: null,
                  }, {
                    timestamp: Date.now(),
                  }));
                }}
                styleName="createButton"
                to={E_EXPERIENCE}
              >
                <IconPlus />
                <span styleName="createButtonLabel">Create New Experience</span>
              </Link>
            </div>

            <div styleName="filters">
              <div styleName="toolbar">
                <Input
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  placeholder="Search experiences..."
                  theme={searchInputTheme}
                  value={searchQuery}
                >
                  <IconSearch styleName="searchIcon" />
                </Input>
                <div styleName="sortWrap">
                  <Dropdown
                    label="Sort by:"
                    onChange={(e) => {
                      setSortOrder(e.target.value as SortOrderT);
                    }}
                    options={SORT_OPTIONS}
                    theme={sortDropdownTheme}
                    value={sortOrder}
                  />
                </div>
              </div>
              <div styleName="pills">
                <button
                  onClick={() => {
                    setSelectedModality('All');
                  }}
                  styleName={selectedModality === 'All' ? 'pill active' : 'pill'}
                  type="button"
                >
                  All
                </button>
                {MODALITIES.map((modality) => (
                  <button
                    key={modality}
                    onClick={() => {
                      setSelectedModality(modality);
                    }}
                    styleName={selectedModality === modality ? 'pill active' : 'pill'}
                    type="button"
                  >
                    {modality}
                  </button>
                ))}
              </div>
            </div>

            <div styleName="sessionNodes">{sessionNodes}</div>
          </div>
        </div>
      </div>
    </PageWithVideoBackground>
  );
};

export default PastSessions;
